/**
 * Transport-agnostic message channel for online co-op. The lockstep and lobby
 * layers only see `NetChannel`; WebRTC (webrtc.ts), a same-machine
 * BroadcastChannel transport (dev testing, no server) and an in-memory
 * loopback (tests, with simulated latency/jitter) all implement it.
 *
 * Messages are JSON values. Large messages (resync snapshots are ~1 MB) are
 * split into chunks below every transport's per-message limit and reassembled
 * in order, so callers never think about sizes.
 */
export interface NetChannel {
  send(msg: unknown): void;
  onMessage: ((msg: unknown) => void) | null;
  onClose: ((reason: string) => void) | null;
  close(reason?: string): void;
  readonly isOpen: boolean;
}

/** Stay well below RTCDataChannel's conservative 64 KiB interop limit. */
export const CHUNK_CHARS = 48 * 1024;

interface Chunk { '~': number; k: number; n: number; d: string }
const isChunk = (v: unknown): v is Chunk => !!v && typeof v === 'object' && '~' in (v as object) && typeof (v as Chunk).d === 'string';

/**
 * Base class: subclasses implement `sendRaw(text)` and call `receiveRaw(text)`
 * / `closed(reason)`. Handles JSON framing and chunking.
 */
export abstract class FramedChannel implements NetChannel {
  onMessage: ((msg: unknown) => void) | null = null;
  onClose: ((reason: string) => void) | null = null;
  private nextChunkId = 1;
  private assembling: { id: number; parts: string[]; n: number } | null = null;
  private done = false;

  protected abstract sendRaw(text: string): void;
  protected abstract closeRaw(): void;
  abstract get isOpen(): boolean;

  send(msg: unknown): void {
    if (this.done) return;
    const text = JSON.stringify(msg);
    if (text.length <= CHUNK_CHARS) {
      this.sendRaw(text);
      return;
    }
    const id = this.nextChunkId++;
    const n = Math.ceil(text.length / CHUNK_CHARS);
    for (let k = 0; k < n; k++) this.sendRaw(JSON.stringify({ '~': id, k, n, d: text.slice(k * CHUNK_CHARS, (k + 1) * CHUNK_CHARS) } satisfies Chunk));
  }

  protected receiveRaw(text: string): void {
    if (this.done) return;
    let value: unknown;
    try {
      value = JSON.parse(text);
    } catch {
      return; // never let a malformed packet kill the session
    }
    if (isChunk(value)) {
      if (!this.assembling || this.assembling.id !== value['~']) this.assembling = { id: value['~'], parts: [], n: value.n };
      this.assembling.parts[value.k] = value.d;
      if (value.k !== value.n - 1) return;
      const whole = this.assembling.parts.join('');
      this.assembling = null;
      try {
        value = JSON.parse(whole);
      } catch {
        return;
      }
    }
    this.onMessage?.(value);
  }

  protected closed(reason: string): void {
    if (this.done) return;
    this.done = true;
    this.onClose?.(reason);
  }

  close(reason = 'closed'): void {
    if (this.done) return;
    this.closeRaw();
    this.closed(reason);
  }
}

/* -------------------------------------------------------------------------- */
/* In-memory loopback with a controllable clock (tests)                        */
/* -------------------------------------------------------------------------- */

export interface LinkConditions {
  /** One-way latency in ms. */
  latency: number;
  /** Extra random one-way delay in [0, jitter) ms; ordering is preserved (reliable ordered channel). */
  jitter: number;
}

class LoopbackEnd extends FramedChannel {
  peer!: LoopbackEnd;
  open = true;
  lastDeliverAt = 0;
  constructor(private link: LoopbackLink) {
    super();
  }
  get isOpen(): boolean {
    return this.open;
  }
  protected sendRaw(text: string): void {
    if (!this.open) return;
    this.link.enqueue(this, this.peer, text);
  }
  protected closeRaw(): void {
    this.open = false;
    const peer = this.peer;
    this.link.enqueueClose(peer);
  }
  deliver(text: string): void {
    this.receiveRaw(text);
  }
  remoteClosed(): void {
    this.open = false;
    this.closed('peer closed');
  }
}

/**
 * A pair of connected channels whose deliveries happen only when `pump(now)`
 * is called — so tests control time exactly. `random` defaults to a tiny
 * deterministic LCG so jittery tests are reproducible.
 */
export class LoopbackLink {
  readonly a: LoopbackEnd;
  readonly b: LoopbackEnd;
  private queue: { at: number; to: LoopbackEnd; text: string | null }[] = [];
  now = 0;
  private seed = 12345;
  constructor(public conditions: LinkConditions = { latency: 0, jitter: 0 }, private random: () => number = () => ((this.seed = (this.seed * 1103515245 + 12345) >>> 0) / 0x100000000)) {
    this.a = new LoopbackEnd(this);
    this.b = new LoopbackEnd(this);
    this.a.peer = this.b;
    this.b.peer = this.a;
  }
  enqueue(from: LoopbackEnd, to: LoopbackEnd, text: string): void {
    const at = Math.max(from.lastDeliverAt, this.now + this.conditions.latency + this.random() * this.conditions.jitter);
    from.lastDeliverAt = at;
    this.queue.push({ at, to, text });
  }
  enqueueClose(to: LoopbackEnd): void {
    this.queue.push({ at: this.now + this.conditions.latency, to, text: null });
  }
  /** Deliver everything due by `now` (in scheduled order). */
  pump(now: number): void {
    this.now = now;
    for (;;) {
      this.queue.sort((x, y) => x.at - y.at);
      if (!this.queue.length || this.queue[0].at > now) break;
      const m = this.queue.shift()!;
      if (m.text === null) m.to.remoteClosed();
      else if (m.to.open) m.to.deliver(m.text);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Same-machine transport (two tabs/windows, no server) for development        */
/* -------------------------------------------------------------------------- */

/**
 * One end of a same-origin BroadcastChannel link. Used by the `?coop=local`
 * development mode so co-op can be tested in two browser windows without
 * signalling or WebRTC. Both ends must agree on `name`, and each end knows its
 * own id and the other end's id so several guests can share one lobby.
 */
export class BroadcastChannelLink extends FramedChannel {
  private bc: BroadcastChannel;
  private open = true;
  constructor(name: string, private self: string, private other: string) {
    super();
    this.bc = new BroadcastChannel(name);
    this.bc.onmessage = (e: MessageEvent) => {
      const data = e.data as { to?: string; from?: string; text?: string; bye?: boolean };
      if (data?.to !== this.self || data.from !== this.other) return;
      if (data.bye) {
        this.open = false;
        this.bc.close();
        this.closed('peer closed');
      } else if (typeof data.text === 'string') this.receiveRaw(data.text);
    };
    window.addEventListener('pagehide', () => this.close('page closed'));
  }
  get isOpen(): boolean {
    return this.open;
  }
  protected sendRaw(text: string): void {
    if (this.open) this.bc.postMessage({ to: this.other, from: this.self, text });
  }
  protected closeRaw(): void {
    if (!this.open) return;
    this.open = false;
    this.bc.postMessage({ to: this.other, from: this.self, bye: true });
    this.bc.close();
  }
}
